import { IUploadedMeeting } from 'types';
import { Typography, Box, Button, Paper } from '@mui/material';
import { useLocale } from '@koopajs/react';
import { DocumentList } from '../DocumentList';
import { Link as RouterLink, useHistory } from 'react-router-dom';
import { useCommitteeName } from 'components/hooks/useCommitteeName';
import React, { useCallback } from 'react';
import { formatDate } from 'utils/DateTime/formatDate';

interface IUploadedMeetingCardProps {
  uploadedMeeting: IUploadedMeeting;
}

export const UploadedMeetingCard: React.FC<IUploadedMeetingCardProps> = (props) => {
  const { uploadedMeeting } = props;

  const committeeType = useCommitteeName(uploadedMeeting?.committeeId || '');

  const hasDocuments = Boolean(uploadedMeeting?.documentsIds?.length);

  const { t, locale } = useLocale();
  const keyPrefix = 'PastMeetings.PastMeetingCard';

  const history = useHistory();

  const renderMeetingActionButton = useCallback(() => {
    return (
      <Button
        component={RouterLink}
        to={{
          pathname: `/uploaded-meetings/${uploadedMeeting.id}`,
          search: history.location?.search
        }}
        data-cy="uploaded-meeting-card_action-button"
      >
        {t(keyPrefix + '.viewMeetingLabel')}
      </Button>
    );
  }, [uploadedMeeting.id, committeeType, history.location]);

  return (
    <Paper
      variant="outlined"
      sx={{
        padding: '24px',
        display: 'flex',
        flexDirection: 'column',
        my: 2
      }}
      data-cy="uploaded-meeting-card"
    >
      <Box>
        {/* Title & Button */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'baseline'
          }}
        >
          <Typography
            sx={{
              mb: 2,
              fontSize: '16px',
              overflow: 'hidden'
            }}
            variant="h3"
          >
            <Box sx={{ display: { xs: 'flex', sm: 'block' }, flexDirection: 'column' }}>
              {formatDate({
                isoString: uploadedMeeting.startDateTime,
                format: 'DATE_HUGE',
                locale,
                isUTC: true
              })}
            </Box>
            <Box
              sx={{
                mt: 2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '100%'
              }}
              data-cy="uploaded-meeting-card_title"
            >
              {uploadedMeeting?.title}
            </Box>
          </Typography>
          <Box sx={{ display: { xs: 'none', md: 'block' } }}>{renderMeetingActionButton()}</Box>
        </Box>

        {/* Committee */}
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          {committeeType && (
            <Typography variant="body2" sx={{ opacity: '0.6' }}>
              {committeeType}
            </Typography>
          )}
        </Box>

        {/* Documents */}
        {hasDocuments && (
          <DocumentList
            path={`/uploaded-meetings/${uploadedMeeting.id}/documents`}
            isAccordion={true}
            accordionOptions={{ documentsVisible: 0 }}
            showDivider
          />
        )}
      </Box>
      <Box sx={{ display: { xs: 'block', md: 'none' }, mt: 2 }}>{renderMeetingActionButton()}</Box>
    </Paper>
  );
};
